import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield, Users, Clock, Award, Leaf, Truck, Phone, ArrowRight } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";
import SEOHead from "@/components/SEOHead";
import { organizationSchema } from "@/utils/schemas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const values = [
  {
    icon: Shield,
    title: "Fully Insured",
    description: "Complete liability coverage for your peace of mind on every job."
  },
  {
    icon: Users,
    title: "Firefighter Owned",
    description: "The same dedication and reliability we bring to emergency response."
  },
  {
    icon: Clock,
    title: "Punctual Service",
    description: "We respect your time and always arrive when we say we will."
  },
  {
    icon: Leaf,
    title: "Eco-Friendly",
    description: "Responsible disposal with recycling and donation when possible."
  },
  {
    icon: Award,
    title: "Professional",
    description: "Uniformed crews, clean trucks, and courteous service every time."
  },
  {
    icon: Truck,
    title: "Equipped",
    description: "Modern equipment to handle any job, big or small, efficiently."
  }
];

const defaultCMS = {
  hero: {
    headline1: "Firefighter Owned.",
    headline2: "Mission Driven.",
    description: "Overhaul Ops LLC is a veteran firefighter-owned and operated junk removal company proudly serving the Dallas-Fort Worth metroplex."
  },
  story: {
    title: "From Station to Service",
    image: "https://customer-assets.emergentagent.com/job_overhaul-ops-crm/artifacts/9h398j8z_image.png",
    content: `Founded in 2025 by Aaron Harper, Overhaul Ops was born from the same values that guide firefighters every day: reliability, hard work, and service to the community.

The name "Overhaul" comes from firefighting terminology – it's the final phase of fire suppression where crews ensure a fire is completely out by searching for and extinguishing hidden fires. Just like in the fire service, we don't leave until the job is done right.

As a firefighter-owned company, we bring that same dedication, discipline, and attention to detail to every junk removal, hauling, and clean out project we undertake. We're fully insured, always punctual, and committed to exceeding your expectations.`
  },
  owner: {
    eyebrow: "Meet the Owner",
    name: "Aaron Harper",
    image: "https://customer-assets.emergentagent.com/job_overhaul-ops-crm/artifacts/luhclfqf_image.png",
    content: `Hello, my name is Aaron. I recently started a locally owned junk removal business offering junk removal, material hauling, and light demolition services. I operate as a one-man crew, taking on projects during my off days from the firehouse.

When I'm not on duty, I prefer to stay productive and engaged rather than sitting idle. This business allows me to put that work ethic to use while helping homeowners and businesses reclaim their space, clean up clutter, and tackle projects they've been putting off.

I pride myself on showing up on time, working efficiently, and treating every job with the same care and respect I bring to my career in the fire service. Whether it's a small cleanout or a larger hauling or demolition job, my goal is simple: provide honest, reliable service and leave every customer better off than when I arrived.`
  }
};

// Skeleton loader component for About page
const AboutSkeleton = () => (
  <div data-testid="about-page" className="pt-20">
    <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl animate-pulse">
          <div className="h-4 w-24 bg-white/20 rounded mb-4"></div>
          <div className="h-16 w-96 bg-white/20 rounded mb-6"></div>
          <div className="h-6 w-80 bg-white/20 rounded"></div>
        </div>
      </div>
    </section>
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-pulse">
          <div className="aspect-square bg-gray-200 rounded-sm"></div>
          <div>
            <div className="h-4 w-24 bg-gray-200 rounded mb-4"></div>
            <div className="h-12 w-64 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-3">
              <div className="h-4 w-full bg-gray-200 rounded"></div>
              <div className="h-4 w-full bg-gray-200 rounded"></div>
              <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default function About() {
  const { formatted_phone, phone_link } = useSiteConfig();
  const [cms, setCms] = useState(defaultCMS);
  const [cmsLoaded, setCmsLoaded] = useState(false);
  
  useEffect(() => {
    const fetchCMS = async () => {
      try {
        const response = await axios.get(`${API}/cms/page/about`);
        if (response.data?.length > 0) {
          const content = { ...defaultCMS };
          response.data.forEach(item => {
            if (item.section && item.content) {
              content[item.section] = { ...content[item.section], ...item.content };
            }
          });
          setCms(content);
        }
      } catch (e) { /* use defaults */ }
      finally {
        setCmsLoaded(true);
      }
    };
    fetchCMS();
  }, []);
  
  // Show skeleton while CMS loads
  if (!cmsLoaded) {
    return <AboutSkeleton />;
  }
  
  return (
    <div data-testid="about-page" className="pt-20">
      <SEOHead
        title="About Us - Firefighter Owned Junk Removal"
        description="Overhaul Ops LLC is a firefighter-owned and operated junk removal company serving the DFW metroplex. Fully insured, reliable, and eco-friendly."
        keywords="about overhaul ops, firefighter junk removal, DFW junk removal company, insured junk removal"
        schemas={[organizationSchema()]}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "About", url: "/about" }]}
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              About Us
            </span>
            <h1 
              className="text-5xl md:text-7xl font-black text-white uppercase tracking-wide leading-none mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {cms.hero.headline1}<br />
              <span className="text-[#b51a00]">{cms.hero.headline2}</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {cms.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section data-testid="our-story" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={cms.story?.image || defaultCMS.story.image}
                alt="Aaron Harper - Owner of Overhaul Ops with Tarrant County Fire Department Engine 570"
                className="w-full rounded-sm shadow-2xl"
                width="600"
                height="400"
                loading="lazy"
              />
            </div>
            <div>
              <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                Our Story
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 mb-6 text-[#0f0f0f]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {cms.story?.title || "From Station to Service"}
              </h2>
              <div className="section-divider mb-8"></div>
              <div className="space-y-6 text-[#4a4a4a] leading-relaxed">
                {(cms.story?.content || defaultCMS.story.content).split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section data-testid="our-values" className="py-20 md:py-32 bg-[#f4f4f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              What Drives Us
            </span>
            <h2 
              className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 text-[#0f0f0f]"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Our Values
            </h2>
            <div className="section-divider mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-white p-8 rounded-sm border-l-4 border-[#b51a00] shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="icon-box rounded-sm mb-4 w-14 h-14">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 
                  className="text-xl font-bold uppercase tracking-tight mb-2 text-[#0f0f0f]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {value.title}
                </h3>
                <p className="text-[#4a4a4a]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Owner Section */}
      <section data-testid="owner-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                {cms.owner?.eyebrow || defaultCMS.owner.eyebrow}
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 mb-6 text-[#0f0f0f]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {cms.owner?.name || defaultCMS.owner.name}
              </h2>
              <div className="section-divider mb-8"></div>
              <div className="space-y-6 text-[#4a4a4a] leading-relaxed">
                {(cms.owner?.content || defaultCMS.owner.content).split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={phone_link}>
                  <Button 
                    className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-6 py-4"
                  >
                    <Phone className="mr-2 w-4 h-4" />
                    {formatted_phone}
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    variant="outline"
                    className="border-2 border-[#b51a00] text-[#b51a00] hover:bg-[#b51a00] hover:text-white font-bold uppercase tracking-wider rounded-sm px-6 py-4"
                  >
                    Request Quote
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src={cms.owner?.image || defaultCMS.owner.image}
                alt="Aaron Harper - Firefighter and Owner of Overhaul Ops LLC"
                className="w-full rounded-sm shadow-2xl aspect-square object-cover"
                width="600"
                height="600"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#b51a00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Contact us today for a free, no-obligation quote. We're here to help!
          </p>
          <Link to="/contact">
            <Button 
              data-testid="about-cta-btn"
              className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
            >
              Get Your Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
